import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "../components/ui/carousel";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const features = [
  {
    title: "Nous nous mettons à votre place",
    description: "Chaque projet immobilier est unique. Nous prenons le temps de comprendre vos attentes, votre mode de vie, vos contraintes et vos objectifs afin de vous accompagner avec une approche sur-mesure.\n\nNotre rôle ne se limite pas à trouver ou vendre un bien : c'est parce que nous aurons compris vos besoins que nous pourrons vous aider à prendre les bonnes décisions, au bon moment, avec une vision claire et stratégique.\n\nLe meilleur équilibre entre le bon prix, l'adéquation avec vos attentes et vos rêves, en toute sécurité, avec un objectif simple : votre bonheur."
  },
  {
    title: "Nous vous facilitons la vie",
    description: "Un projet immobilier demande du temps, de l'énergie et une vraie disponibilité.\nNous prenons en charge 100% du travail, que vous soyez acheteur ou vendeur : recherches, visites, dossier, échanges, vérifications et suivi administratif.\nNous vous retirons la charge mentale du projet : chaque étape se déroule avec méthode et sérénité.\n\nVotre rôle est simple : nous aider à comprendre vos besoins, valider votre budget ou le prix de votre bien, choisir parmi les options que nous vous présentons.\nLe reste, c'est notre métier, jusqu'à la signature chez le notaire."
  },
  {
    title: "Nous nous engageons pleinement à vos côtés",
    description: "Nous abordons chaque projet avec une implication totale, comme si c'était le nôtre. Réactivité, énergie, rigueur et disponibilité : nous mobilisons tout ce qui peut faire avancer votre transaction.\nNous activons notre réseau, recherchons activement les meilleures opportunités, sollicitons les bons interlocuteurs et négocions avec détermination pour défendre vos intérêts.\n\nNotre rémunération étant liée à la réussite de votre projet, notre engagement est total à chaque étape."
  },
  {
    title: "Nous vous faisons gagner de l'argent",
    description: "Nous défendons votre intérêt financier avec une approche concrète et exigeante.\n\nQue ce soit à la vente pour augmenter la valeur de votre bien en optimisant sa présentation ou dans le cadre d'une recherche en trouvant la meilleure opportunité que nous négocions avec détermination pour vous faire acheter au meilleur prix.\n\nNos honoraires sont compétitifs et les négociations sont menées avec méthode, chiffres et connaissance du marché. Et parce qu'un client satisfait devient souvent notre meilleur ambassadeur, nous rémunérons aussi les apporteurs d'affaires qui nous recommandent."
  }
];

const testimonials = [
  {
    text: "La discrétion et le professionnalisme de Marie-Caroline sont remarquables. Elle a su comprendre nos exigences et nous présenter des biens d'exception, avec une connaissance pointue du marché parisien haut de gamme.",
    author: "Jean – 75008 Paris"
  },
  {
    text: "Vivant à Londres, je cherchais un pied-à-terre à Paris. Malgré la distance, l'accompagnement de Dean a été irréprochable : visites virtuelles, compte-rendus détaillés, gestion complète jusqu'à la signature. Un service sur-mesure exceptionnel puisque Emplacement-immo s'est chargé de meubler et de décorer mon appartement jusqu'à la brosse à dents !",
    author: "Thierry – 75002 Paris"
  },
  {
    text: "J'ai acheté mon premier appartement, j'étais perdue face à la complexité du marché parisien et surchargée de boulot, Laurence m'a guidé avec pédagogie et bienveillance. En 3 mois, nous avons trouvé mon appartement idéal dans le 11ème, elle s'est occupée de tout jusqu'à la signature finale. Un grand merci !",
    author: "Camille – 75011 Paris"
  },
  {
    text: "Nous avions des critères très précis et un budget serré. L'équipe a su nous orienter avec justesse, sans jamais nous faire perdre de temps. Ils nous ont présenté plusieurs appartements et on a eu un coup de cœur pour le 3ème, une expérience vraiment rassurante que nous recommandons !",
    author: "Vincent et Léa – 75010 Paris"
  },
  {
    text: "Après le décès de ma mère, son appartement était encombré et pas mis en valeur. L'équipe a tout pris en main avec beaucoup de délicatesse : tri, rangement, nettoyage, petits travaux, décoration. En dix jours, le bien était méconnaissable. Il a été vendu dès la première visite. Nous avons été soulagés, accompagnés, et très impressionnés par leur efficacité.",
    author: "Simon – Paris 75005"
  },
  {
    text: "Notre appartement était en vente depuis plusieurs mois sans résultat, après avoir été surestimé par une première agence. Emplacement-immo nous a présenté une analyse claire, chiffres à l'appui, avec les dernières ventes comparables du quartier. Ils ont repositionné le prix, refait les photos, retravaillé l'annonce en valorisant les écoles, le marché et la vie de quartier. Quinze jours plus tard, l'appartement était vendu.",
    author: "Gaby et Loïc – 75018 Paris"
  }
];

const properties = [
  {
    id: 1,
    image: "https://res.cloudinary.com/dpeldfqpi/image/upload/v1774736517/00_salle_a%CC%80_manger_scdcbf.webp",
    type: "Maison",
    location: "17ème arrondissement",
    status: "Trouvé"
  },
  {
    id: 2,
    image: "https://res.cloudinary.com/dpeldfqpi/image/upload/v1779997135/1_salon1cap_gloscr.png",
    type: "Appartement",
    location: "18ème arrondissement",
    status: "Trouvé"
  },
  {
    id: 3,
    image: "https://res.cloudinary.com/dpeldfqpi/image/upload/v1774736900/01_IMG_6098_Original_ibnrsb.jpg",
    type: "Duplex",
    location: "2ème arrondissement",
    status: "Trouvé"
  }
];

export const HomePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative h-screen flex items-center justify-center"
      >
        {/* Background - Hero image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_vente-achat-paris/artifacts/nqheg9i1_terrasse%20lolo.jpg"
            alt="Terrasse parisienne d'exception"
            className="w-full h-full object-cover"
            data-testid="hero-bg-image"
          />
          {/* Dark overlay for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-background"></div>
        </div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative z-10 text-center px-6 max-w-5xl mx-auto"
        >
          <h1
            data-testid="hero-title"
            className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white mb-4"
          >
            Recherches et Ventes
          </h1>
          <p
            data-testid="hero-subtitle"
            className="text-xl sm:text-2xl lg:text-3xl font-semibold text-gold mb-10 tracking-widest uppercase"
          >
            Emplacement-Immo
          </p>
          
          {/* 3 CTA Buttons — reduced size, no icons */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <Link to="/recherches" className="block">
              <div className="bg-white/95 backdrop-blur-sm border border-gold/40 hover:border-gold px-5 py-5 transition-all duration-300 group">
                <h3 className="font-serif text-text-primary text-lg group-hover:text-gold transition-colors">
                  Je cherche un bien
                </h3>
              </div>
            </Link>
            <Link to="/ventes" className="block">
              <div className="bg-white/95 backdrop-blur-sm border border-gold/40 hover:border-gold px-5 py-5 transition-all duration-300 group">
                <h3 className="font-serif text-text-primary text-lg group-hover:text-gold transition-colors">
                  Je vends mon bien
                </h3>
              </div>
            </Link>
            <Link to="/valorisation" className="block">
              <div className="bg-white/95 backdrop-blur-sm border border-gold/40 hover:border-gold px-5 py-5 transition-all duration-300 group">
                <h3 className="font-serif text-text-primary text-lg group-hover:text-gold transition-colors">
                  Je valorise mon bien
                </h3>
              </div>
            </Link>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <div className="w-6 h-10 border-2 border-gold/50 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 12, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-gold rounded-full"
            />
          </div>
        </motion.div>
      </section>

      {/* Notre Mission Section - closer to hero CTAs */}
      <section data-testid="about-section" className="py-16 md:py-20 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="max-w-4xl mx-auto"
          >
            <motion.h2
              variants={fadeInUp}
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-12"
            >
              Notre mission
            </motion.h2>
            <motion.div variants={fadeInUp} className="space-y-6 text-text-secondary text-lg leading-relaxed">
              <p>
                Acheter ou vendre est une décision de vie et un engagement patrimonial majeur, qui mérite une approche rigoureuse, honnête et personnalisée.
              </p>
              <p className="text-gold font-semibold">
                Notre mission est claire : défendre vos intérêts avec la même exigence que si nous le faisions pour nous-mêmes.
              </p>
              <ul className="space-y-4 list-none">
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Nous prenons le temps de bien comprendre vos attentes pour réussir notre mission.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Nous vous faisons gagner un temps précieux en prenant en charge les visites et la constitution du dossier administratif et technique pour l'achat ou la vente.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Nous établissons la juste valeur de votre bien, chiffres à l'appui, pour que vous achetiez ou vendiez au bon prix.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Copropriété, syndic, travaux à venir, solvabilité : nous vérifions tout pour sécuriser votre transaction et vous permettre d'anticiper les années à venir.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Nous valorisons votre bien pour vous permettre de le vendre dans les meilleures conditions.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-gold mt-1">•</span>
                  <span>Notre ambition est de fédérer une communauté de clients heureux, fidèles et qui auront plaisir à nous recommander. Et pour remercier nos ambassadeurs de nous apporter de nouveaux clients, nous les rémunérons sur une partie de nos honoraires.</span>
                </li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Haussmannian Interior Image */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8"
        >
          <div className="relative aspect-[21/9] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/19616630/pexels-photo-19616630.jpeg?auto=compress&cs=tinysrgb&w=1920"
              alt="Intérieur appartement haussmannien parisien avec parquet et cheminée"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent"></div>
          </div>
        </motion.div>
      </section>

      {/* Pourquoi nous choisir Section */}
      <section data-testid="why-us-section" className="py-24 md:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-16"
          >
            Pourquoi nous choisir ?
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`feature-${index}`}
                className="bg-background border border-line p-8 hover:border-gold/50 transition-colors duration-500"
              >
                <h3 className="text-gold font-bold text-xl mb-6">
                  {feature.title}
                </h3>
                <div className="text-text-secondary text-sm leading-relaxed whitespace-pre-line">
                  {feature.description}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Image between Stats and Testimonials */}
      <section className="py-0 bg-background">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="max-w-7xl mx-auto px-6 lg:px-8 py-12"
        >
          <div className="relative aspect-[21/9] overflow-hidden">
            <img
              src="https://res.cloudinary.com/dpeldfqpi/image/upload/v1774889884/Capture_d_e%CC%81cran_2026-02-01_a%CC%80_15.19.21_kbvup2.png"
              alt="Emplacement-immo"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="py-24 md:py-32 bg-surface">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-16"
          >
            Témoignages
          </motion.h2>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div
                    data-testid={`testimonial-${index}`}
                    className="bg-white border border-line p-8 h-full flex flex-col shadow-sm"
                  >
                    <p className="text-text-secondary text-sm leading-relaxed flex-grow mb-6 italic">
                      "{testimonial.text}"
                    </p>
                    <p className="text-gold text-sm font-semibold">
                      — {testimonial.author}
                    </p>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center gap-4 mt-8">
              <CarouselPrevious className="relative inset-0 translate-y-0 bg-transparent border-gold text-gold hover:bg-gold/10" />
              <CarouselNext className="relative inset-0 translate-y-0 bg-transparent border-gold text-gold hover:bg-gold/10" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* Nos trouvailles Section */}
      <section data-testid="properties-preview-section" className="py-24 md:py-32 bg-background">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary text-center mb-16"
          >
            Nos trouvailles
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {properties.map((property, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`property-preview-${index}`}
                className="group"
              >
                <Link to={`/nos-trouvailles#property-${property.id}`} className="block">
                  <div className="relative overflow-hidden aspect-[4/3] mb-4">
                    <img
                      src={property.image}
                      alt={property.type}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className={`px-3 py-1 text-xs font-bold ${
                        property.status === "Trouvé" 
                          ? "bg-gold text-white" 
                          : "bg-navy text-white"
                      }`}>
                        {property.status}
                      </span>
                    </div>
                  </div>
                  <h3 className="font-serif text-text-primary text-2xl mb-1 group-hover:text-gold transition-colors">{property.type}</h3>
                  <p className="text-text-secondary text-sm">{property.location}</p>
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/nos-trouvailles">
              <Button
                data-testid="voir-trouvailles"
                variant="outline"
                className="border-navy text-navy hover:bg-navy hover:text-white font-semibold px-10 py-6 rounded-none"
              >
                Voir toutes nos trouvailles
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="cta-section" className="py-24 md:py-32 bg-surface">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-text-primary mb-6">
              Prêt à vous lancer ?
            </h2>
            <p className="text-text-secondary text-lg mb-10 max-w-2xl mx-auto">
              Que vous cherchiez l'appartement idéal, souhaitiez vendre votre bien au meilleur 
              prix ou bénéficiez de nos services de valorisation, notre équipe est à votre écoute.
            </p>
            <Link to="/contact">
              <Button
                data-testid="cta-contact"
                className="bg-navy text-white hover:bg-navy hover:border-gold border border-navy font-semibold px-10 py-6 text-lg rounded-none"
              >
                Nous contacter
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
